import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showSuccess } from "@/utils";
import apiClient from "@/services/apiClient";
import BrandButton from "@/components/ui/BrandButton";
import PasswordInput from "@/components/ui/PasswordInput";
import PasswordRequirements from "@/components/ui/PasswordRequirements";
import LanguageSelector from "@/layout/LanguageSelector";
import "@/pages/page.css";
import { AUTH_ISSUER } from "@/config";
import { getRecoveryToken, clearRecoveryToken } from "@/services/tokenService";

function SetNewPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const resetCompletedRef = useRef(false);

  useEffect(() => {
    if (!resetCompletedRef.current && !getRecoveryToken()) {
      navigate("/forgot-password", { replace: true });
    }
  }, [navigate]);

  const passwordMeetsRequirements =
    newPassword.length >= 8 &&
    newPassword.length <= 20 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /\d/.test(newPassword) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

  const passwordsMatch =
    newPassword === confirmPassword && confirmPassword !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError(t("auth.passwordsDoNotMatch", "Passwords do not match."));
      return;
    }

    if (newPassword.length < 8) {
      setError(
        t(
          "auth.passwordMinLength",
          "Password must be at least 8 characters.",
        ),
      );
      return;
    }

    if (newPassword.length > 20) {
      setError(
        t(
          "auth.passwordMaxLength",
          "Password must not exceed 20 characters.",
        ),
      );
      return;
    }

    if (!/[A-Z]/.test(newPassword)) {
      setError(
        t(
          "auth.passwordUppercase",
          "Password must contain at least one uppercase letter.",
        ),
      );
      return;
    }

    if (!/[a-z]/.test(newPassword)) {
      setError(
        t(
          "auth.passwordLowercase",
          "Password must contain at least one lowercase letter.",
        ),
      );
      return;
    }

    if (!/\d/.test(newPassword)) {
      setError(
        t(
          "auth.passwordNumber",
          "Password must contain at least one number.",
        ),
      );
      return;
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setError(
        t(
          "auth.passwordSpecial",
          "Password must contain at least one special character.",
        ),
      );
      return;
    }

    if (!getRecoveryToken()) {
      setError(
        t(
          "auth.recoverySessionExpired",
          "Your password reset session has expired. Please request a new code."
        )
      );
      setTimeout(() => navigate("/forgot-password", { replace: true }), 3000);
      return;
    }

    setLoading(true);

    try {
      await apiClient.post(`${AUTH_ISSUER}/reset-password`, {
        new_password: newPassword,
      });

      resetCompletedRef.current = true;
      clearRecoveryToken();
      showSuccess(
        t(
          "auth.passwordUpdatedSuccess",
          "Password updated. Please log in with your new password.",
        ),
      );
      navigate("/login", { replace: true });
    } catch (err) {
      const isAuthError = err.response && (err.response.status === 401 || err.response.status === 403);
      if (isAuthError) {
        clearRecoveryToken();
        setError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t("auth.otpInvalidOrExpired", "Invalid or expired code. Please try again.")
        );
        setTimeout(() => navigate("/forgot-password", { replace: true }), 3000);
      } else {
        setError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            t("common.serverError", "Server error. Please try again.")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <main className="content" style={{ marginLeft: 0 }}>
        <div className="d-flex justify-content-end p-3">
          <LanguageSelector />
        </div>
        <div className="row justify-content-center mt-3">
          <div className="col-md-5 col-lg-4">
            <div className="card card-radius">
              <div className="card-body">
                <h3 className="text-center mb-2">
                  {t("auth.setNewPassword", "Set New Password")}
                </h3>
                <p className="text-center text-muted mb-4">
                  {t(
                    "auth.setNewPasswordSubtitle",
                    "Choose a new password for your account.",
                  )}
                </p>
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="newPassword" className="form-label">
                      {t("auth.newPassword", "New Password")}
                    </label>
                    <PasswordInput
                      id="newPassword"
                      name="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                    />
                    <PasswordRequirements password={newPassword} />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">
                      {t("auth.confirmNewPassword", "Confirm New Password")}
                    </label>
                    <PasswordInput
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      minLength={8}
                      disabled={!passwordMeetsRequirements}
                    />
                  </div>
                  <BrandButton
                    type="submit"
                    className="w-100"
                    disabled={loading || !passwordsMatch}
                  >
                    {loading
                      ? t("common.saving", "Saving...")
                      : t("auth.setPassword", "Set Password")}
                  </BrandButton>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SetNewPassword;
